import { httpApi } from '@app/api/http.api';
import { ContentModel } from '@app/models/ContentModel';
import { UserApiPaginationModel, UserListPaginatedModel, UserModel } from '@app/models/UserModel';
import { FeatureComment, FeatureRequest } from '@app/pages/FeatureRequests/types';
import {
  FlagStats, FlaggedSerial, FlaggedSerialHit,
} from '@app/pages/FlaggedSerials/types';
import { Feedback, FeedbackListModel, FeedbackQuery } from '@app/pages/Feedback/types';
import {
  AppBuild, AppRelease, DeploymentListModel, Deployment, UnreleasedFeature,
} from '@app/pages/Releases/types';
import qs from 'query-string';
import { FirmwareModel } from '@app/pages/Firmwares/types';
import { Tip } from '@app/pages/Tips/types';
import { DebugAction, DebugSession, EnqueueActionPayload } from '@app/models/DebugSessionModel';
import { HealthAdminResponse } from '@app/models/HealthDataModel';
import {
  ShopifyFilterOptions,
  ShopifyOrderListModel,
  ShopifyOrderModel,
  ShopifyOrderQuery,
  ShopifyOrderStats,
} from '@app/models/ShopifyOrderModel';

export interface NotificationPayload {
  heading: string;
  message: string;
}

// Staff-only, paged server-side, newest first.
export const getFeedbacks = (params: FeedbackQuery = {}): Promise<FeedbackListModel> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<FeedbackListModel>(`/api/v1/contact-us/?${query}`, {}).then(({ data }) => data);
};

export const getFeedback = (id: number): Promise<Feedback> =>
  httpApi.get<Feedback>(`/api/v1/contact-us/${id}/`, {}).then(({ data }) => data);

/** Emails and notifies the customer; returns the updated row. */
export const postFeedbackResponse = (id: number, response: string): Promise<Feedback> =>
  httpApi.post<Feedback>(`/api/v1/contact-us/${id}/response/`, { response }).then(({ data }) => data);

// Release log (staff-only). Releases and builds are small enough to load whole.
export const getAppReleases = (): Promise<AppRelease[]> =>
  httpApi.get<AppRelease[]>('/api/v1/release-log/app-releases/').then(({ data }) => data);

export const updateAppReleaseSummary = (id: number, summary: string): Promise<AppRelease> =>
  httpApi.patch<AppRelease>(`/api/v1/release-log/app-releases/${id}/`, { summary }).then(({ data }) => data);

export const getAppBuilds = (): Promise<AppBuild[]> =>
  httpApi.get<AppBuild[]>('/api/v1/release-log/app-builds/').then(({ data }) => data);

export const getDeployments = (
  params: { page?: number; page_size?: number; target?: string } = {},
): Promise<DeploymentListModel> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<DeploymentListModel>(`/api/v1/release-log/deployments/?${query}`).then(({ data }) => data);
};

export const updateDeploymentSummary = (id: number, summary: string): Promise<Deployment> =>
  httpApi.patch<Deployment>(`/api/v1/release-log/deployments/${id}/`, { summary }).then(({ data }) => data);

export const getUnreleasedFeatures = (): Promise<UnreleasedFeature[]> =>
  httpApi.get<UnreleasedFeature[]>('/api/v1/release-log/unreleased/').then(({ data }) => data);

export const createUnreleasedFeature = (title: string): Promise<UnreleasedFeature> =>
  httpApi.post<UnreleasedFeature>('/api/v1/release-log/unreleased/', { title, is_active: true }).then(({ data }) => data);

export const updateUnreleasedFeature = (id: number, is_active: boolean): Promise<UnreleasedFeature> =>
  httpApi.patch<UnreleasedFeature>(`/api/v1/release-log/unreleased/${id}/`, { is_active }).then(({ data }) => data);

export const deleteUnreleasedFeature = (id: number): Promise<void> =>
  httpApi.delete(`/api/v1/release-log/unreleased/${id}/`).then(() => undefined);

export const getUsers = (params: UserApiPaginationModel): Promise<UserListPaginatedModel> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<UserListPaginatedModel>(`/api/v1/user/?${query}`, {}).then(({ data }) => data);
}

// Same filter params as the users list (minus paging) so exports match the table.
export type UserExportFilters = Omit<UserApiPaginationModel, 'page' | 'page_size' | 'filters'>;

export const exportUsers = (filters: UserExportFilters): Promise<{ blob: Blob; filename: string }> => {
  const query = qs.stringify(filters, { skipNull: true, skipEmptyString: true });
  return httpApi.get(`/api/v1/user/export/?${query}`, { responseType: 'blob' }).then((res) => {
    const disposition = (res.headers && res.headers['content-disposition']) || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'users_export.xlsx';
    return { blob: res.data as Blob, filename };
  });
};

export const getUser = (id: string): Promise<UserModel> =>
  httpApi.get<UserModel>(`/api/v1/user/${id}/`, {}).then(({ data }) => data);

// Admin-only: per-user, time-bucketed Apple Health data for the user detail view.
export const getUserHealthData = (
  userId: number,
  params: { from?: string; to?: string; bucket?: 'day' | 'hour' } = {},
): Promise<HealthAdminResponse> => {
  const query = qs.stringify({ user: userId, ...params }, { skipNull: true, skipEmptyString: true });
  return httpApi.get<HealthAdminResponse>(`/api/v1/health-data/admin/?${query}`, {}).then(({ data }) => data);
};

export const updateUser = (user: UserModel): Promise<UserModel> =>
  httpApi.patch<UserModel>(`/api/v1/user/${user.id}/`, user).then(({ data }) => data);

export const changePassword = (userId: number, password: string): Promise<UserModel> =>
  httpApi.post<UserModel>(`/api/v1/admin-user/${userId}/update-password/`, { password }).then(({ data }) => data);

export const getContent = (): Promise<ContentModel[]> =>
  httpApi.get<ContentModel[]>('/modules/articles/article/').then(({ data }) => data);

export const postContent = (content: any): Promise<ContentModel> =>
  httpApi.post<ContentModel>('/modules/articles/article/', content, { headers: { 'Content-Type': 'multipart/form-data' } }).then(({ data }) => data);

export const updateContent = (id: number, content: any): Promise<ContentModel> =>
  httpApi.patch<ContentModel>(`/modules/articles/article/${id}/`, content, { headers: { 'Content-Type': 'multipart/form-data' } }).then(({ data }) => data);

export const deleteContent = (id: number): Promise<any> =>
  httpApi.delete<ContentModel[]>(`/modules/articles/article/${id}/`).then(({ data }) => data);

export const postNotification = (notification: NotificationPayload): Promise<Notification> =>
  httpApi.post<Notification>('/api/v1/admin-notifications/', { ...notification }).then(({ data }) => data);

export const getFeatureRequests = (): Promise<FeatureRequest[]> =>
  httpApi.get<FeatureRequest[]>('/api/v1/features/', {}).then(({ data }) => data);

export const updateFeatureRequest = (id: number, data: any): Promise<FeatureRequest> =>
  httpApi.patch<FeatureRequest>(`/api/v1/features/${id}/`, data).then(({ data }) => data);

export const deleteFeatureRequest = (id: number): Promise<any> =>
  httpApi.delete<any>(`/api/v1/features/${id}/`).then(({ data }) => data);

export interface NewFeatureRequest {
  description: string;
  /** 'ease', 'wavon', or 'ease,wavon' to target both. */
  brand: string;
  category?: number | null;
}

/** `as_team` attributes the post to the shared support account, not the
 *  individual staff member. Ignored by the backend for non-staff callers. */
export const createFeatureRequest = (payload: NewFeatureRequest): Promise<FeatureRequest> =>
  httpApi
    .post<FeatureRequest>('/api/v1/features/', { ...payload, as_team: true })
    .then(({ data }) => data);

export const getFeatureComments = (featureId: number): Promise<FeatureComment[]> =>
  httpApi
    .get<FeatureComment[]>(`/api/v1/features/${featureId}/comments/`, {})
    .then(({ data }) => data);

export const postFeatureComment = (featureId: number, comment: string): Promise<FeatureComment> =>
  httpApi
    .post<FeatureComment>(`/api/v1/features/${featureId}/comments/`, { comment, as_team: true })
    .then(({ data }) => data);

/** Staff may remove any comment; the backend enforces it. Used for moderation. */
export const deleteFeatureComment = (featureId: number, commentId: number): Promise<void> =>
  httpApi
    .delete<void>(`/api/v1/features/${featureId}/comments/${commentId}/`)
    .then(({ data }) => data);

export const likeFeatureComment = (commentId: number): Promise<FeatureComment> =>
  httpApi.post<FeatureComment>(`/api/v1/comments/${commentId}/like/`, {}).then(({ data }) => data);

export const unlikeFeatureComment = (commentId: number): Promise<FeatureComment> =>
  httpApi.post<FeatureComment>(`/api/v1/comments/${commentId}/unlike/`, {}).then(({ data }) => data);

export const getFirmwares = (): Promise<FirmwareModel[]> =>
  httpApi.get<FirmwareModel[]>('/api/v1/firmware/', {}).then(({ data }) => data);

export const postFirmware = (content: any): Promise<FirmwareModel> =>
  httpApi.post<FirmwareModel>('/api/v1/firmware/', content, { headers: { 'Content-Type': 'multipart/form-data' } }).then(({ data }) => data);


export const updateFirmware = (id: number, data: any): Promise<FirmwareModel> =>
  httpApi.patch<FirmwareModel>(`/api/v1/firmware/${id}/`, data).then(({ data }) => data);

export const deleteFirmware = (id: number): Promise<any> =>
  httpApi.delete<any>(`/api/v1/firmware/${id}/`).then(({ data }) => data);

export const getTips = (): Promise<Tip[]> =>
  httpApi.get<Tip[]>('/api/v1/tip/', {}).then(({ data }) => data);

export const postTip = (tip: Partial<Tip>): Promise<Tip> =>
  httpApi.post<Tip>('/api/v1/tip/', tip).then(({ data }) => data);

export const updateTip = (id: number, tip: Partial<Tip>): Promise<Tip> =>
  httpApi.patch<Tip>(`/api/v1/tip/${id}/`, tip).then(({ data }) => data);

export const deleteTip = (id: number): Promise<any> =>
  httpApi.delete<any>(`/api/v1/tip/${id}/`).then(({ data }) => data);

export interface GoogleTokenResponse {
  access_token: string;
}

export const getGoogleAccessToken = (): Promise<GoogleTokenResponse> =>
  httpApi.get('/api/v1/google-token/', {}).then(({ data }) => data);

// --- Admin dashboard analytics (staff-only endpoints) ---
//
// Every analytics getter takes a single params object and builds the query with
// qs.stringify (null/empty values dropped). Callers pass the relevant subset of
// the global filters: `brand` (all-user charts), `from`/`to` (time-series),
// `segment` (sitting/custom), plus a couple of chart-specific params. `brand`
// of 'all' is passed through harmlessly — the backend only acts on ease/wavon.
export type AnalyticsParams = Record<string, string | number | undefined | null>;

const analyticsGet = (path: string, params: AnalyticsParams = {}): Promise<any> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get(`/api/v1/${path}${query ? `?${query}` : ''}`).then(({ data }) => data);
};

export const getUsersDemographics = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('users-demographics/', params);

export const getSessionPerScreen = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('session-per-screen/', params);

// Mode Usage is not brand-scoped (no user link); it takes a date range only.
export const getModeCount = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('mode-count/', params);

export const getMonthlyActiveUser = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('monthly-active-user/', params);

export const getUserRetention = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('retention-rate/', params);

export const getChurnRate = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('churn-rate/', params);

export const getAppVersions = (): Promise<any> =>
  httpApi.get('/api/v1/app-versions/', {}).then(({ data }) => data);

export const getPlatformBreakdown = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('platform-breakdown/', params);

// Brand breakdown is never brand-scoped (it is the split itself).
export const getBrandBreakdown = (): Promise<any> =>
  analyticsGet('brand-breakdown/');

export const getAgeDistribution = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('age-distribution/', params);

export const getUserGrowth = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('user-growth/', params);

export const getCushionUsage = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('cushion-usage/', params);

export const getBatteryDistribution = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('battery-distribution/', params);

// Firmware is not brand-scoped (Cushion has no user link).
export const getFirmwareDistribution = (): Promise<any> =>
  analyticsGet('firmware-distribution/');

export const getComfortTrends = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('comfort-trends/', params);

export const getHealthAggregates = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('health-aggregates/', params);

export const getTopFeatures = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('top-features/', params);

export const getGoalsDistribution = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('goals-distribution/', params);

export const getRemindersSummary = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('reminders-summary/', params);

export const getOtaDiagnostics = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('ota-diagnostics/', params);

export const getSupportSummary = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('support-summary/', params);

export const getSittingAnalytics = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('sitting-analytics/', params);

export const getCustomSettings = (params: AnalyticsParams = {}): Promise<any> =>
  analyticsGet('custom-settings/', params);

// --- Debug / troubleshooting mode ---

// Returns the active debug session for a user, or null if none.
export const getActiveDebugSession = (userId: number): Promise<DebugSession | null> =>
  httpApi
    .get<DebugSession[]>(`/api/v1/debug-sessions/?user=${userId}&is_active=true`, {})
    .then(({ data }) => (Array.isArray(data) && data.length ? data[0] : null));

// Past (inactive) sessions for a user, most recent first.
export const getPastDebugSessions = (userId: number): Promise<DebugSession[]> =>
  httpApi
    .get<DebugSession[]>(`/api/v1/debug-sessions/?user=${userId}&is_active=false`, {})
    .then(({ data }) => (Array.isArray(data) ? data : []));

export const getDebugSession = (sessionId: number): Promise<DebugSession> =>
  httpApi.get<DebugSession>(`/api/v1/debug-sessions/${sessionId}/`, {}).then(({ data }) => data);

export const enableDebugSession = (userId: number, agentIdentifier: string): Promise<DebugSession> =>
  httpApi
    .post<DebugSession>('/api/v1/debug-sessions/', { user: userId, agent_identifier: agentIdentifier })
    .then(({ data }) => data);

export const disableDebugSession = (sessionId: number): Promise<DebugSession> =>
  httpApi.post<DebugSession>(`/api/v1/debug-sessions/${sessionId}/disable/`, {}).then(({ data }) => data);

export const enqueueDebugAction = (sessionId: number, payload: EnqueueActionPayload): Promise<DebugAction> =>
  httpApi.post<DebugAction>(`/api/v1/debug-sessions/${sessionId}/actions/`, payload).then(({ data }) => data);

export const cancelDebugAction = (sessionId: number, actionId: number): Promise<DebugAction> =>
  httpApi
    .post<DebugAction>(`/api/v1/debug-sessions/${sessionId}/cancel-action/`, { action_id: actionId })
    .then(({ data }) => data);



// ---- Shopify orders (admin) -------------------------------------------------
// Staff-only. Mirrors getUserHealthData above: the backend gates on is_staff.

export const getShopifyOrders = (params: ShopifyOrderQuery = {}): Promise<ShopifyOrderListModel> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<ShopifyOrderListModel>(`/api/v1/shopify/admin/orders/?${query}`, {}).then(({ data }) => data);
};

/** A single user's orders, for the user popup. */
export const getUserShopifyOrders = (userId: number): Promise<ShopifyOrderListModel> =>
  getShopifyOrders({ user: userId });

export const getShopifyOrderStats = (params: ShopifyOrderQuery = {}): Promise<ShopifyOrderStats> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<ShopifyOrderStats>(`/api/v1/shopify/admin/orders/stats/?${query}`, {}).then(({ data }) => data);
};

export const getShopifyFilterOptions = (): Promise<ShopifyFilterOptions> =>
  httpApi.get<ShopifyFilterOptions>('/api/v1/shopify/admin/orders/filter-options/', {}).then(({ data }) => data);

export const linkShopifyOrder = (orderItemId: number, userId: number): Promise<ShopifyOrderModel> =>
  httpApi
    .post<ShopifyOrderModel>(`/api/v1/shopify/admin/orders/${orderItemId}/link/`, { user_id: userId })
    .then(({ data }) => data);

export const unlinkShopifyOrder = (orderItemId: number): Promise<ShopifyOrderModel> =>
  httpApi.post<ShopifyOrderModel>(`/api/v1/shopify/admin/orders/${orderItemId}/unlink/`, {}).then(({ data }) => data);


// ---- Lost / stolen serials -------------------------------------------------

export interface FlaggedSerialQuery {
  page?: number;
  page_size?: number;
  search?: string;
  reason?: string;
  is_active?: string;
}

export interface FlaggedHitQuery {
  page?: number;
  page_size?: number;
  search?: string;
  is_resolved?: string;
  also_has_replacement?: string;
  user?: number;
}

interface Paginated<T> {
  count: number;
  results: T[];
}

export const getFlaggedSerials = (params: FlaggedSerialQuery = {}): Promise<Paginated<FlaggedSerial>> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<Paginated<FlaggedSerial>>(`/api/v1/flagged-serials/?${query}`, {})
    .then(({ data }) => data);
};

export const createFlaggedSerial = (payload: Partial<FlaggedSerial>): Promise<FlaggedSerial> =>
  httpApi.post<FlaggedSerial>('/api/v1/flagged-serials/', payload).then(({ data }) => data);

export const updateFlaggedSerial = (id: number, payload: Partial<FlaggedSerial>): Promise<FlaggedSerial> =>
  httpApi.patch<FlaggedSerial>(`/api/v1/flagged-serials/${id}/`, payload).then(({ data }) => data);

export interface BulkFlagResult {
  added: string[];
  already_flagged: string[];
  invalid: string[];
}

export const bulkFlagSerials = (serials: string, reason: string, notes = ''): Promise<BulkFlagResult> =>
  httpApi.post<BulkFlagResult>('/api/v1/flagged-serials/bulk/', { serials, reason, notes })
    .then(({ data }) => data);

/** Removes the flag and, by cascade, any detections recorded against it. */
export const deleteFlaggedSerial = (id: number): Promise<void> =>
  httpApi.delete<void>(`/api/v1/flagged-serials/${id}/`).then(({ data }) => data);

export const getFlaggedHits = (params: FlaggedHitQuery = {}): Promise<Paginated<FlaggedSerialHit>> => {
  const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
  return httpApi.get<Paginated<FlaggedSerialHit>>(`/api/v1/flagged-serial-hits/?${query}`, {})
    .then(({ data }) => data);
};

export const resolveFlaggedHit = (id: number, notes = ''): Promise<FlaggedSerialHit> =>
  httpApi.post<FlaggedSerialHit>(`/api/v1/flagged-serial-hits/${id}/resolve/`, { notes })
    .then(({ data }) => data);

export const reopenFlaggedHit = (id: number): Promise<FlaggedSerialHit> =>
  httpApi.post<FlaggedSerialHit>(`/api/v1/flagged-serial-hits/${id}/reopen/`, {})
    .then(({ data }) => data);

export const getFlagStats = (): Promise<FlagStats> =>
  httpApi.get<FlagStats>('/api/v1/flagged-serial-hits/stats/', {}).then(({ data }) => data);
